import type { Difficulty } from '../bot/types';
import type { HeroId } from '../types';

export const PROFILE_KEY = 'critter-clatter.profile';
export const PROFILE_BACKUP_KEY = 'critter-clatter.profile.bak';
export const PROFILE_QUARANTINE_KEY = 'critter-clatter.profile.quarantine';
export const PROFILE_VERSION = 1;

/** Keys that mean this browser already played or tuned the glen. Preferences stay outside the profile. */
export const LEGACY_KEYS = [
  'critter-clatter.loadouts',
  'critter-clatter.powerup-vfx',
  'critter.volume',
  'critter.duelMusic',
  'critter.format',
  'critter.sound',
  'critter.coach',
  'critter.coach.replay',
] as const;

export type CurrencyId = 'acorns' | 'honey';

export type LedgerReason =
  | 'migration'
  | 'match'
  | 'road'
  | 'streak'
  | 'unlock'
  | 'upgrade'
  | 'cosmetic'
  | 'stake'
  | 'charm'
  | 'trick'
  | 'log'
  | 'spin'
  | 'purchase';

export interface LedgerEntry {
  seq: number;
  at: number;
  reason: LedgerReason;
  currencies: Partial<Record<CurrencyId, number>>;
  trophies: number;
  ref?: string;
}

export type StreakSave = 'locked' | 'ready' | 'used';

export interface StreakState {
  eventId: string | null;
  endsAt: number | null;
  wins: number;
  save: StreakSave;
  claimed: number[];
  closedIds: string[];
}

export interface CosmeticState {
  ribbon: boolean;
  /** Bought shelf ids. Garland and lantern counts live beside this list. */
  owned: string[];
  equipped: {
    outfit: Partial<Record<HeroId, string>>;
    board: string | null;
    hub: string[];
  };
  garlands: number;
  lanterns: number;
}

export interface Profile {
  v: 1;
  seq: number;
  accountId: null;
  wallet: Record<CurrencyId, number>;
  trophies: number;
  bestTrophies: number;
  critters: Record<HeroId, { unlocked: boolean }>;
  tricks: Partial<Record<string, { level: number }>>;
  /** Copies on hand. Free Tricks are not stored. Absent on a save that has not migrated. */
  trickStock: Record<string, number>;
  /** Staked Charm copies. Twig uses `regrowAt` instead of a stack. */
  charms: Record<string, { count: number; regrowAt: number | null }>;
  /** Prize Box timer. The save field stays `log` so older saves still open. */
  log: { readyAt: number; adCuts: number; adDay: string; stickers: number };
  /** One free Daily Spin per local day, plus spins bought from a pack. Absent on a save from before the wheel. */
  dailySpin: { day: string; used: boolean; banked: number };
  /** 2 after honey ×10, the upgrade refund, and the welcome Charm pack. */
  economyRev: number;
  loadouts: Record<HeroId, string[]>;
  road: { claimed: number[] };
  streak: StreakState;
  cosmetics: CosmeticState;
  entitlements: Record<string, { at: number }>;
  settled: string[];
  /** Set after the first duel ends or is quit, so the lesson does not return. */
  tutorialSeen: boolean;
  lastSeenAt: number;
  ledger: LedgerEntry[];
  checksum: string;
}

export interface TrophyBand {
  name: string;
  min: number;
  bot: Difficulty;
  win: number;
  loss: number;
  floor: number;
}

export interface RoadNode {
  at: number;
  acorns: number;
  honey: number;
  critter?: HeroId;
  /** Granted at level 1 when this stop is claimed. */
  trick?: string;
}

export interface StreakMark {
  wins: number;
  acorns: number;
  honey: number;
  /** Five wins is the celebrated prize. Ten wins stays a rare bonus. */
  role?: 'headline' | 'bonus';
}

export type MatchOutcome = 'win' | 'loss' | 'tie';
